import { isCancelledError } from '@tanstack/react-query';
import { noop } from 'lodash';
import { makeAutoObservable, reaction } from 'mobx';
import { create, persist } from 'mobx-persist';

import { OwnApartment } from '@models/apartment.types';
import { queryClient } from '@providers/QueryProvider';
import { authStore } from '@stores/authStore';
import { createOnAuthenticateListener } from '@stores/authStore/authStore.utils';
import { isLocalOrTestEnv } from '@utils/featureFlags';

import { fetchApartmentsQuery } from './apartment.queries';

export class ApartmentStore {
  @persist selectedApartmentID: string | null = null;
  apartments: OwnApartment[] | null = null;

  isFetching = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    createOnAuthenticateListener(noop, this.clear);
  }

  getParkingArea(areaId: string | undefined) {
    if (!areaId) return undefined;

    return this.selectedApartment?.realestate.parkingAreas.find(
      (area) => area.id === areaId
    );
  }

  getParkingSpace(spaceId: string | undefined) {
    if (!spaceId) return undefined;

    return this.selectedApartment?.realestate.parkingSpaces.find(
      (space) => space.id === spaceId
    );
  }

  get selectedApartment() {
    return (
      this.apartments?.find(
        (apartment) => apartment.apartmentId === this.selectedApartmentID
      ) ?? undefined
    );
  }

  get getApartmentsWithSauna() {
    return (
      this.apartments?.filter((apt) =>
        apt.realestate.realEstateFeatures.includes('REAL_ESTATE_SAUNA')
      ) ?? undefined
    );
  }

  get selectedApartmentRealestateId() {
    return this.selectedApartment?.realestateID;
  }

  setSelectedApartmentID(apartmentID: typeof this.selectedApartmentID) {
    this.selectedApartmentID = apartmentID;
  }

  clear() {
    this.apartments = [];
    this.selectedApartmentID = null;
    this.isFetching = false;
  }

  async fetchAndSetApartments() {
    // early exit if fetching
    if (this.isFetching) return;

    this.isFetching = true;

    try {
      const fetchedApartments =
        await queryClient.fetchQuery(fetchApartmentsQuery);

      if (!fetchedApartments?.length) throw Error('User has no apartments');

      this.apartments = fetchedApartments;

      const isSelectedApartmentIdValid = !!this.apartments?.some(
        (apartment) => apartment.apartmentId === this.selectedApartmentID
      );

      if (!this.selectedApartment || !isSelectedApartmentIdValid) {
        this.selectedApartmentID = this.apartments[0]!.apartmentId;
      }
    } catch (e) {
      // Do not throw if query was cancelled. This can happen if user is abruptly logged out when their authentication tokens have expired.
      if (isCancelledError(e)) {
        if (isLocalOrTestEnv) {
          console.warn('fetchApartments query was cancelled');
        }

        return;
      }

      throw e;
    } finally {
      this.isFetching = false;
    }
  }
}

export const apartmentStore = new ApartmentStore();

create({})('apartment-store', apartmentStore);

// Refresh apartments every time idToken changes. This happens when the user logs in, or when contracts are refreshed, thus possibly adding to or updating apartment data.
reaction(
  () => authStore.idToken,
  (token) => {
    // If user has logged out, do not fetch apartments
    if (!token) return;

    apartmentStore.fetchAndSetApartments();
  }
);
